import { APP_INITIALIZER, NgModule, LOCALE_ID, ErrorHandler } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localeEn from '@angular/common/locales/en';
import { ServiceWorkerModule } from '@angular/service-worker';

import * as Sentry from '@sentry/angular';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { provideEventPlugins } from '@taiga-ui/event-plugins';

import { BazisHeaderInterceptor } from '@bazis/interceptors/header.interceptor';
import { BazisAuthInterceptor } from '@bazis/interceptors/auth.interceptor';
import { BazisHttpErrorsInterceptor } from '@bazis/interceptors/http-errors.interceptor';
import { BazisSignInterceptor } from '@bazis/interceptors/sign.interceptor';
import { BazisHttpTimeoutInterceptor } from '@bazis/interceptors/timeout-error.interceptor';
import { BazisConfirmOrgInfoInterceptor } from '@bazis/interceptors/confirm-org-info.interceptor';
import { BazisLocaleService } from '@bazis/shared/services/locale.service';
import { LocalDatePipe } from '@bazis/shared/pipes/local-date.pipe';
import { isLocal } from '@bazis/utils';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { BazisConfigurationService } from '@bazis/configuration.service';
import {
    ALL_SEARCH_MODELS_DATA,
    ROLE_SEARCH_MODEL_IDS_DATA,
} from '@bazis/shared/services/search.service';
import { DISABLE_NOTIFICATIONS_ROLES_DATA } from '@bazis/shared/services/notification.service';
import { BazisAnalyticService } from '@bazis/analytics/shared/service/analytic.service';
import { API_URL, BAZIS_API_V } from '@bazis/shared/services/srv.service';

import { AppComponent } from '@app/app.component';
import { AppRoutingModule } from '@app/app-routing.module';
import {
    ALL_SEARCH_MODELS,
    ConfigurationService,
    DISABLE_NOTIFICATIONS_ROLES,
    ROLE,
    ROLE_ACCOUNT_TYPE,
    ROLE_SEARCH_MODEL_IDS,
} from '@app/configuration.service';
import { SharedModule } from '@shared/shared.module';
import { SwAuthService } from '@shared/services/auth.service';
import { TranslocoRootModule } from '@shared/modules/transloco-root.module';
import { AnalyticService } from '@pages/analytics/analytic.service';

registerLocaleData(localeRu, 'ru');
registerLocaleData(localeEn, 'en');

export function loadConfiguration(configService: ConfigurationService) {
    return () => configService.loadConfiguration();
}

const interceptorProviders = [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisHeaderInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisConfirmOrgInfoInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisSignInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisAuthInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisHttpTimeoutInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisHttpErrorsInterceptor,
        multi: true,
    },
];
const sentryProviders = [];
if (!isLocal()) {
    sentryProviders.push({
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler({
            showDialog: false,
            logErrors: true,
        }),
    });
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        BrowserAnimationsModule,
        SharedModule,
        TranslocoRootModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            // enabled: environment.production,
            // Register the ServiceWorker as soon as the app is stable
            // or after 30 seconds (whichever comes first).
            enabled: !isLocal(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
        NgxMaskDirective,
        NgxMaskPipe,
    ],
    providers: [
        { provide: BazisAnalyticService, useExisting: AnalyticService },
        { provide: BazisAuthService, useExisting: SwAuthService },
        { provide: BazisConfigurationService, useExisting: ConfigurationService },
        {
            provide: APP_INITIALIZER,
            useFactory: loadConfiguration,
            deps: [ConfigurationService],
            multi: true,
        },
        Title,
        {
            provide: API_URL,
            useValue: '/api/v1',
        },
        {
            provide: BAZIS_API_V,
            useValue: '2',
        },
        {
            provide: LOCALE_ID,
            deps: [BazisLocaleService],
            useFactory: (localeService) => localeService.locale,
        },
        ...interceptorProviders,
        ...sentryProviders,
        LocalDatePipe,
        provideEnvironmentNgxMask(),
        { provide: ALL_SEARCH_MODELS_DATA, useValue: ALL_SEARCH_MODELS },
        { provide: ROLE_SEARCH_MODEL_IDS_DATA, useValue: ROLE_SEARCH_MODEL_IDS },
        { provide: DISABLE_NOTIFICATIONS_ROLES_DATA, useValue: DISABLE_NOTIFICATIONS_ROLES },
        { provide: 'ROLE', useValue: ROLE },
        { provide: 'ROLE_ACCOUNT_TYPE', useValue: ROLE_ACCOUNT_TYPE },
        provideHttpClient(withInterceptorsFromDi()),
        provideHttpClient(withFetch()),
        provideEventPlugins(),
    ],
})
export class AppModule {}
